import {
  WORKER_PIN_FORMAT_LABEL,
  WORKER_PIN_HINT,
  WORKER_PIN_LENGTH,
  generateWorkerPin,
  isValidWorkerPin,
} from '@insolitum/types';
import {
  matchesWorkerSearch,
  measuredModuleCount,
  orphanModules,
  workerFullName,
  workerInitials,
  workerModuleAccess,
} from '@insolitum/utils';
import {
  HardHat,
  Plus,
  Search,
  Edit2,
  Trash2,
  X,
  AlertCircle,
  Phone,
  Mail,
  KeyRound,
  Clock,
  CheckCircle,
  XCircle,
  Loader2,
  Save,
  Calendar,
  Package,
  Globe,
  Layers,
  ChevronRight,
  RefreshCw,
  Download,
} from 'lucide-react';
import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { ModuleAccessChips } from '../../components/admin/workers/ModuleAccessChips';
import {
  SekcjaPowiazaniaReceptora,
  StanPinuReceptora,
  type WyborOsobyReceptora,
} from '../../components/admin/workers/PowiazanieReceptora';
import { RolaWOrganizacji } from '../../components/admin/workers/RolaWOrganizacji';
import { formatRelativeDate } from '../../components/admin/workers/tone';
import {
  setWorkerModuleAccess,
  useWorkerCatalog,
  WORKER_WINDOW_LABELS,
  WORKER_WINDOW_OPTIONS,
  type CatalogRow,
  type WorkerWindowDays,
} from '../../hooks/useWorkerInsights';
import { supabase } from '../../lib/supabase';
import { WORKER_MODULES, WORKER_MODULE_SLUGS } from '../../modules/registry';
import { useAuth } from '../../providers/AuthProvider';

import { ROLE_ZARZADZAJACE } from './devices/AgenciReceptora';
import {
  ODSWIEZANIE_STANU_PINOW_MS,
  agenciDoOsob,
  komunikatBleduReceptora,
  opisPinuZReceptora,
  pinCzeka,
  pinZReceptora,
  zdanieAgentaZaStarego,
  zdaniePinuOsoby,
  type AgenciDoOsob,
  type StanReceptoraPracownika,
} from './devices/pracownicyReceptora';
import {
  listaAgentow,
  pobierzPinZReceptora,
  powiazZReceptorem,
  receptorCoreDostepny,
  stanLicencjiReceptora,
  stanReceptoraPracownikow,
  type AgentReceptora,
} from './devices/receptorCoreApi';
import { najnowszyNaPracownika, wymagajaUwagi, type WierszKadr } from './kadryStan';
import { opiszStanTozsamosci } from './wtyczkiManifest';

// Organy, do których przypisuje się pracownika, biorą się teraz z REJESTRU
// (`src/modules/registry.ts`), a nie z ręcznie przepisanej tablicy w tym pliku.
// Tamta lista miała cztery pozycje i rozjeżdżała się z rejestrem po cichu —
// Aegis wypadł z niej na tyle długo, że karta pracownika nie miała jak
// powiedzieć, że ten organ go dotyczy. Jedno źródło znaczy, że dołożenie
// modułu do rejestru samo dokłada go tutaj.

// Supported languages — NEXUS speaks every language, so the picker is searchable and
// covers the whole world (ISO 639-1). Native name + English name (for Latin-script
// search) + a representative flag (🌐 where a language has no single country).
// The crew's most common languages are surfaced first.
const SUPPORTED_LANGUAGES: { code: string; name: string; english: string; flag: string }[] = [
  { code: 'pl', name: 'Polski', english: 'Polish', flag: '🇵🇱' },
  { code: 'uk', name: 'Українська', english: 'Ukrainian', flag: '🇺🇦' },
  { code: 'ru', name: 'Русский', english: 'Russian', flag: '🇷🇺' },
  { code: 'be', name: 'Беларуская', english: 'Belarusian', flag: '🇧🇾' },
  { code: 'en', name: 'English', english: 'English', flag: '🇬🇧' },
  { code: 'de', name: 'Deutsch', english: 'German', flag: '🇩🇪' },
  { code: 'es', name: 'Español', english: 'Spanish', flag: '🇪🇸' },
  { code: 'fr', name: 'Français', english: 'French', flag: '🇫🇷' },
  { code: 'ar', name: 'العربية', english: 'Arabic', flag: '🇸🇦' },
  { code: 'zh', name: '中文', english: 'Chinese', flag: '🇨🇳' },
  { code: 'hi', name: 'हिन्दी', english: 'Hindi', flag: '🇮🇳' },
  { code: 'bn', name: 'বাংলা', english: 'Bengali', flag: '🇧🇩' },
  { code: 'pt', name: 'Português', english: 'Portuguese', flag: '🇵🇹' },
  { code: 'ja', name: '日本語', english: 'Japanese', flag: '🇯🇵' },
  { code: 'ko', name: '한국어', english: 'Korean', flag: '🇰🇷' },
  { code: 'it', name: 'Italiano', english: 'Italian', flag: '🇮🇹' },
  { code: 'tr', name: 'Türkçe', english: 'Turkish', flag: '🇹🇷' },
  { code: 'nl', name: 'Nederlands', english: 'Dutch', flag: '🇳🇱' },
  { code: 'vi', name: 'Tiếng Việt', english: 'Vietnamese', flag: '🇻🇳' },
  { code: 'th', name: 'ไทย', english: 'Thai', flag: '🇹🇭' },
  { code: 'id', name: 'Bahasa Indonesia', english: 'Indonesian', flag: '🇮🇩' },
  { code: 'fa', name: 'فارسی', english: 'Persian', flag: '🇮🇷' },
  { code: 'ur', name: 'اردو', english: 'Urdu', flag: '🇵🇰' },
  { code: 'ro', name: 'Română', english: 'Romanian', flag: '🇷🇴' },
  { code: 'el', name: 'Ελληνικά', english: 'Greek', flag: '🇬🇷' },
  { code: 'cs', name: 'Čeština', english: 'Czech', flag: '🇨🇿' },
  { code: 'sk', name: 'Slovenčina', english: 'Slovak', flag: '🇸🇰' },
  { code: 'hu', name: 'Magyar', english: 'Hungarian', flag: '🇭🇺' },
  { code: 'sv', name: 'Svenska', english: 'Swedish', flag: '🇸🇪' },
  { code: 'no', name: 'Norsk', english: 'Norwegian', flag: '🇳🇴' },
  { code: 'da', name: 'Dansk', english: 'Danish', flag: '🇩🇰' },
  { code: 'fi', name: 'Suomi', english: 'Finnish', flag: '🇫🇮' },
  { code: 'he', name: 'עברית', english: 'Hebrew', flag: '🇮🇱' },
  { code: 'bg', name: 'Български', english: 'Bulgarian', flag: '🇧🇬' },
  { code: 'hr', name: 'Hrvatski', english: 'Croatian', flag: '🇭🇷' },
  { code: 'sr', name: 'Српски', english: 'Serbian', flag: '🇷🇸' },
  { code: 'sl', name: 'Slovenščina', english: 'Slovenian', flag: '🇸🇮' },
  { code: 'lt', name: 'Lietuvių', english: 'Lithuanian', flag: '🇱🇹' },
  { code: 'lv', name: 'Latviešu', english: 'Latvian', flag: '🇱🇻' },
  { code: 'et', name: 'Eesti', english: 'Estonian', flag: '🇪🇪' },
  { code: 'sq', name: 'Shqip', english: 'Albanian', flag: '🇦🇱' },
  { code: 'hy', name: 'Հայերեն', english: 'Armenian', flag: '🇦🇲' },
  { code: 'ka', name: 'ქართული', english: 'Georgian', flag: '🇬🇪' },
  { code: 'az', name: 'Azərbaycanca', english: 'Azerbaijani', flag: '🇦🇿' },
  { code: 'kk', name: 'Қазақша', english: 'Kazakh', flag: '🇰🇿' },
  { code: 'uz', name: 'Oʻzbekcha', english: 'Uzbek', flag: '🇺🇿' },
  { code: 'ky', name: 'Кыргызча', english: 'Kyrgyz', flag: '🇰🇬' },
  { code: 'tg', name: 'Тоҷикӣ', english: 'Tajik', flag: '🇹🇯' },
  { code: 'tk', name: 'Türkmençe', english: 'Turkmen', flag: '🇹🇲' },
  { code: 'mn', name: 'Монгол', english: 'Mongolian', flag: '🇲🇳' },
  { code: 'my', name: 'မြန်မာ', english: 'Burmese', flag: '🇲🇲' },
  { code: 'km', name: 'ខ្មែរ', english: 'Khmer', flag: '🇰🇭' },
  { code: 'lo', name: 'ລາວ', english: 'Lao', flag: '🇱🇦' },
  { code: 'si', name: 'සිංහල', english: 'Sinhala', flag: '🇱🇰' },
  { code: 'ne', name: 'नेपाली', english: 'Nepali', flag: '🇳🇵' },
  { code: 'pa', name: 'ਪੰਜਾਬੀ', english: 'Punjabi', flag: '🇮🇳' },
  { code: 'gu', name: 'ગુજરાતી', english: 'Gujarati', flag: '🇮🇳' },
  { code: 'ta', name: 'தமிழ்', english: 'Tamil', flag: '🇮🇳' },
  { code: 'te', name: 'తెలుగు', english: 'Telugu', flag: '🇮🇳' },
  { code: 'kn', name: 'ಕನ್ನಡ', english: 'Kannada', flag: '🇮🇳' },
  { code: 'ml', name: 'മലയാളം', english: 'Malayalam', flag: '🇮🇳' },
  { code: 'mr', name: 'मराठी', english: 'Marathi', flag: '🇮🇳' },
  { code: 'or', name: 'ଓଡ଼ିଆ', english: 'Oriya', flag: '🇮🇳' },
  { code: 'as', name: 'অসমীয়া', english: 'Assamese', flag: '🇮🇳' },
  { code: 'am', name: 'አማርኛ', english: 'Amharic', flag: '🇪🇹' },
  { code: 'ti', name: 'ትግርኛ', english: 'Tigrinya', flag: '🇪🇷' },
  { code: 'sw', name: 'Kiswahili', english: 'Swahili', flag: '🇰🇪' },
  { code: 'zu', name: 'isiZulu', english: 'Zulu', flag: '🇿🇦' },
  { code: 'xh', name: 'isiXhosa', english: 'Xhosa', flag: '🇿🇦' },
  { code: 'af', name: 'Afrikaans', english: 'Afrikaans', flag: '🇿🇦' },
  { code: 'st', name: 'Sesotho', english: 'Southern Sotho', flag: '🇱🇸' },
  { code: 'tn', name: 'Setswana', english: 'Tswana', flag: '🇧🇼' },
  { code: 'sn', name: 'ChiShona', english: 'Shona', flag: '🇿🇼' },
  { code: 'ny', name: 'Chichewa', english: 'Chichewa', flag: '🇲🇼' },
  { code: 'yo', name: 'Yorùbá', english: 'Yoruba', flag: '🇳🇬' },
  { code: 'ig', name: 'Igbo', english: 'Igbo', flag: '🇳🇬' },
  { code: 'ha', name: 'Hausa', english: 'Hausa', flag: '🇳🇬' },
  { code: 'ff', name: 'Fulfulde', english: 'Fula', flag: '🌐' },
  { code: 'wo', name: 'Wolof', english: 'Wolof', flag: '🇸🇳' },
  { code: 'bm', name: 'Bamanankan', english: 'Bambara', flag: '🇲🇱' },
  { code: 'ak', name: 'Akan', english: 'Akan', flag: '🇬🇭' },
  { code: 'tw', name: 'Twi', english: 'Twi', flag: '🇬🇭' },
  { code: 'ee', name: 'Eʋegbe', english: 'Ewe', flag: '🇬🇭' },
  { code: 'lg', name: 'Luganda', english: 'Ganda', flag: '🇺🇬' },
  { code: 'rw', name: 'Kinyarwanda', english: 'Kinyarwanda', flag: '🇷🇼' },
  { code: 'rn', name: 'Ikirundi', english: 'Kirundi', flag: '🇧🇮' },
  { code: 'so', name: 'Soomaali', english: 'Somali', flag: '🇸🇴' },
  { code: 'om', name: 'Afaan Oromoo', english: 'Oromo', flag: '🇪🇹' },
  { code: 'mg', name: 'Malagasy', english: 'Malagasy', flag: '🇲🇬' },
  { code: 'ms', name: 'Bahasa Melayu', english: 'Malay', flag: '🇲🇾' },
  { code: 'jv', name: 'Basa Jawa', english: 'Javanese', flag: '🇮🇩' },
  { code: 'su', name: 'Basa Sunda', english: 'Sundanese', flag: '🇮🇩' },
  { code: 'tl', name: 'Tagalog', english: 'Tagalog', flag: '🇵🇭' },
  { code: 'ceb', name: 'Cebuano', english: 'Cebuano', flag: '🇵🇭' },
  { code: 'haw', name: 'ʻŌlelo Hawaiʻi', english: 'Hawaiian', flag: '🌐' },
  { code: 'mi', name: 'Māori', english: 'Māori', flag: '🇳🇿' },
  { code: 'sm', name: 'Gagana Sāmoa', english: 'Samoan', flag: '🇼🇸' },
  { code: 'to', name: 'Lea faka-Tonga', english: 'Tongan', flag: '🇹🇴' },
  { code: 'fj', name: 'Vosa Vakaviti', english: 'Fijian', flag: '🇫🇯' },
  { code: 'is', name: 'Íslenska', english: 'Icelandic', flag: '🇮🇸' },
  { code: 'ga', name: 'Gaeilge', english: 'Irish', flag: '🇮🇪' },
  { code: 'gd', name: 'Gàidhlig', english: 'Scottish Gaelic', flag: '🏴󠁧󠁢󠁳󠁣󠁴󠁿' },
  { code: 'cy', name: 'Cymraeg', english: 'Welsh', flag: '🏴󠁧󠁢󠁷󠁬󠁳󠁿' },
  { code: 'gv', name: 'Gaelg', english: 'Manx', flag: '🇮🇲' },
  { code: 'br', name: 'Brezhoneg', english: 'Breton', flag: '🌐' },
  { code: 'eu', name: 'Euskara', english: 'Basque', flag: '🌐' },
  { code: 'ca', name: 'Català', english: 'Catalan', flag: '🌐' },
  { code: 'gl', name: 'Galego', english: 'Galician', flag: '🌐' },
  { code: 'oc', name: 'Occitan', english: 'Occitan', flag: '🌐' },
  { code: 'co', name: 'Corsu', english: 'Corsican', flag: '🌐' },
  { code: 'sc', name: 'Sardu', english: 'Sardinian', flag: '🌐' },
  { code: 'rm', name: 'Rumantsch', english: 'Romansh', flag: '🇨🇭' },
  { code: 'lb', name: 'Lëtzebuergesch', english: 'Luxembourgish', flag: '🇱🇺' },
  { code: 'fy', name: 'Frysk', english: 'Western Frisian', flag: '🌐' },
  { code: 'mt', name: 'Malti', english: 'Maltese', flag: '🇲🇹' },
  { code: 'mk', name: 'Македонски', english: 'Macedonian', flag: '🇲🇰' },
  { code: 'bs', name: 'Bosanski', english: 'Bosnian', flag: '🇧🇦' },
  { code: 'kmr', name: 'Kurdî', english: 'Kurdish', flag: '🌐' },
  { code: 'ps', name: 'پښتو', english: 'Pashto', flag: '🇦🇫' },
  { code: 'sd', name: 'سنڌي', english: 'Sindhi', flag: '🇵🇰' },
  { code: 'dv', name: 'ދިވެހި', english: 'Divehi', flag: '🇲🇻' },
  { code: 'bo', name: 'བོད་སྐད', english: 'Tibetan', flag: '🌐' },
  { code: 'dz', name: 'རྫོང་ཁ', english: 'Dzongkha', flag: '🇧🇹' },
  { code: 'ug', name: 'ئۇيغۇرچە', english: 'Uyghur', flag: '🌐' },
  { code: 'tt', name: 'Татарча', english: 'Tatar', flag: '🌐' },
  { code: 'ba', name: 'Башҡортса', english: 'Bashkir', flag: '🌐' },
  { code: 'cv', name: 'Чӑваш', english: 'Chuvash', flag: '🌐' },
  { code: 'ce', name: 'Нохчийн', english: 'Chechen', flag: '🌐' },
  { code: 'os', name: 'Ирон', english: 'Ossetian', flag: '🌐' },
  { code: 'ab', name: 'Аҧсуа', english: 'Abkhaz', flag: '🌐' },
  { code: 'kv', name: 'Коми', english: 'Komi', flag: '🌐' },
  { code: 'se', name: 'Davvisámegiella', english: 'Northern Sami', flag: '🌐' },
  { code: 'qu', name: 'Runa Simi', english: 'Quechua', flag: '🌐' },
  { code: 'ay', name: 'Aymar aru', english: 'Aymara', flag: '🇧🇴' },
  { code: 'gn', name: 'Avañeʼẽ', english: 'Guaraní', flag: '🇵🇾' },
  { code: 'ht', name: 'Kreyòl ayisyen', english: 'Haitian Creole', flag: '🇭🇹' },
  { code: 'la', name: 'Latina', english: 'Latin', flag: '🌐' },
  { code: 'eo', name: 'Esperanto', english: 'Esperanto', flag: '🌐' },
  { code: 'yi', name: 'ייִדיש', english: 'Yiddish', flag: '🌐' },
  { code: 'sa', name: 'संस्कृतम्', english: 'Sanskrit', flag: '🌐' },
];

/** Case-insensitive filter over native name, English name, and code. Empty → all. */
function filterLanguages(query: string) {
  const q = query.trim().toLowerCase();
  if (!q) return SUPPORTED_LANGUAGES;
  return SUPPORTED_LANGUAGES.filter(
    (l) =>
      l.name.toLowerCase().includes(q) ||
      l.english.toLowerCase().includes(q) ||
      l.code.toLowerCase() === q ||
      l.code.toLowerCase().startsWith(q)
  );
}

/**
 * Searchable language picker (combobox). Replaces the old fixed 6-option <select> so a
 * worker can be set to any of the world's languages — type to filter by native name,
 * English name, or ISO code. Dark-theme Tailwind to match the employee editor.
 */
function LanguageCombobox({
  value,
  onChange,
}: {
  value: string;
  onChange: (code: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const current = SUPPORTED_LANGUAGES.find((l) => l.code === value) || SUPPORTED_LANGUAGES[0];
  const results = filterLanguages(query);

  const close = () => {
    setOpen(false);
    setQuery('');
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full px-3 py-2.5 bg-gray-700 border border-gray-600 rounded-ids-md text-aurora-text text-left flex items-center justify-between focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
      >
        <span className="flex items-center gap-2">
          <span className="text-lg">{current.flag}</span>
          <span>{current.name}</span>
        </span>
        <ChevronRight
          className={`w-4 h-4 text-gray-400 transition-transform ${open ? 'rotate-90' : ''}`}
        />
      </button>

      {open && (
        <button
          type="button"
          aria-hidden
          tabIndex={-1}
          className="fixed inset-0 z-10 cursor-default"
          onClick={close}
        />
      )}

      {open && (
        <div className="absolute z-20 mt-1 w-full bg-gray-800 border border-gray-600 rounded-ids-md shadow-xl overflow-hidden">
          <div className="p-2 border-b border-gray-700">
            <div className="relative">
              <Search className="w-4 h-4 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
              <input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Szukaj języka…"
                className="w-full pl-8 pr-3 py-2 bg-gray-700 border border-gray-600 rounded-ids-sm text-aurora-text text-sm focus:outline-none focus:ring-1 focus:ring-orange-500"
              />
            </div>
          </div>
          <div className="max-h-56 overflow-y-auto">
            {results.map((lang) => (
              <button
                key={lang.code}
                type="button"
                onClick={() => {
                  onChange(lang.code);
                  close();
                }}
                className={`w-full px-3 py-2 text-left flex items-center gap-2 hover:bg-gray-700 transition-colors ${
                  lang.code === value ? 'bg-orange-500/10 text-orange-300' : 'text-aurora-on-accent'
                }`}
              >
                <span className="text-lg">{lang.flag}</span>
                <span className="flex-1 text-sm">{lang.name}</span>
                {lang.english !== lang.name && (
                  <span className="text-xs text-gray-500">{lang.english}</span>
                )}
                <span className="text-[10px] uppercase text-gray-500">{lang.code}</span>
              </button>
            ))}
            {results.length === 0 && (
              <div className="px-3 py-3 text-sm text-gray-500">Nie znaleziono języka.</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Experience levels
const EXPERIENCE_LEVELS = [
  { value: 'novice', label: 'Początkujący', description: 'Nowy pracownik, wymaga wsparcia' },
  {
    value: 'intermediate',
    label: 'Średniozaawansowany',
    description: 'Samodzielny w podstawowych zadaniach',
  },
  { value: 'expert', label: 'Ekspert', description: 'Pełna samodzielność, może szkolić innych' },
];

// Worker roles
const WORKER_ROLES = [
  { value: 'trainee', label: 'Praktykant', color: 'text-aurora-muted' },
  { value: 'worker', label: 'Pracownik', color: 'text-blue-400' },
  { value: 'senior', label: 'Starszy pracownik', color: 'text-green-400' },
  { value: 'supervisor', label: 'Brygadzista', color: 'text-yellow-400' },
  { value: 'expert', label: 'Ekspert', color: 'text-purple-400' },
];

// Communication styles
const COMMUNICATION_STYLES = [
  { value: 'simple', label: 'Prosty', description: 'Jasne, krótkie komunikaty' },
  { value: 'casual', label: 'Swobodny', description: 'Przyjazny, nieformalny ton' },
  { value: 'formal', label: 'Formalny', description: 'Profesjonalny, oficjalny styl' },
  { value: 'technical', label: 'Techniczny', description: 'Szczegółowe informacje techniczne' },
];

interface WorkerPreferences {
  module_access?: string[];
  [key: string]: unknown;
}

interface ShopFloorWorker {
  id: string;
  organization_id: string;
  /** Konto techniczne pracownika (424/756) — most do roli w `user_organizations` (892). */
  user_id: string | null;
  first_name: string;
  last_name: string;
  pin: string;
  position: string | null;
  email: string | null;
  phone: string | null;
  is_active: boolean;
  last_login_at: string | null;
  preferences: WorkerPreferences;
  // Gamification fields
  preferred_language: string;
  experience_level: string;
  worker_role: string;
  communication_style: string;
  performance_score: number;
  total_points: number;
  current_level: number;
  tenure_start_date: string | null;
  avatar_url: string | null;
  // Employment fields
  employment_type: string;
  contract_type: string;
  can_work_weekends: boolean;
  can_work_holidays: boolean;
  available_all_departments: boolean;
  subject_to_labor_code: boolean;
  hire_date: string | null;
  weekly_hours: number;
  annual_leave_days: number;
  // Timestamps
  created_at: string;
  updated_at: string;
}

// Employment types
const EMPLOYMENT_TYPES = [
  { value: 'employment', label: 'Umowa o pracę (UoP)' },
  { value: 'b2b', label: 'B2B / Samozatrudnienie' },
  { value: 'civil_law', label: 'Umowa zlecenie / dzieło' },
  { value: 'temporary', label: 'Praca tymczasowa' },
  { value: 'internship', label: 'Staż / praktyka' },
];

// Contract types
const CONTRACT_TYPES = [
  { value: 'indefinite', label: 'Na czas nieokreślony' },
  { value: 'fixed_term', label: 'Na czas określony' },
  { value: 'probation', label: 'Okres próbny' },
  { value: 'replacement', label: 'Na zastępstwo' },
  { value: 'seasonal', label: 'Sezonowa' },
];

interface WorkerFormData {
  first_name: string;
  last_name: string;
  pin: string;
  position: string;
  email: string;
  phone: string;
  module_access: string[];
  // Gamification fields
  preferred_language: string;
  experience_level: string;
  worker_role: string;
  communication_style: string;
  // Employment fields
  employment_type: string;
  contract_type: string;
  can_work_weekends: boolean;
  can_work_holidays: boolean;
  available_all_departments: boolean;
  subject_to_labor_code: boolean;
  hire_date: string;
  weekly_hours: number;
  annual_leave_days: number;
}

const initialFormData: WorkerFormData = {
  first_name: '',
  last_name: '',
  pin: '',
  position: '',
  email: '',
  phone: '',
  // Nowy pracownik dostaje wszystkie organy — zgodnie z permisywną regułą
  // `brak deklaracji ⇒ dostęp do wszystkiego`, którą realizuje
  // `workerModuleAccess`. Poprzednia trójka była zaszytą listą sprzed Aegisa
  // i cicho odcinała nowo dodanych ludzi od organów, których na niej nie było.
  module_access: [...WORKER_MODULE_SLUGS],
  preferred_language: 'pl',
  experience_level: 'novice',
  worker_role: 'worker',
  communication_style: 'simple',
  // Employment defaults
  employment_type: 'employment',
  contract_type: 'indefinite',
  can_work_weekends: false,
  can_work_holidays: false,
  available_all_departments: true,
  subject_to_labor_code: true,
  hire_date: '',
  weekly_hours: 40,
  annual_leave_days: 26,
};

/** Który wycinek kartoteki pokazujemy. */
type FilterMode = 'active' | 'all' | 'gaps';

const FILTER_LABELS: Record<FilterMode, string> = {
  active: 'Aktywni',
  all: 'Wszyscy',
  gaps: 'Luki i sygnały',
};

export function WorkersPage() {
  const { currentOrganization, hasRole } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [windowDays, setWindowDays] = useState<WorkerWindowDays>(30);
  const {
    rows,
    isLoading,
    isLoadingStats,
    error: catalogError,
    statsUnavailable,
    refresh: fetchWorkers,
  } = useWorkerCatalog(windowDays);

  /**
   * Znacznik „Saldeo" przy pracowniku — jedno zapytanie do mapy tożsamości
   * (711) przez RLS. Tabela nie niesie danych osobowych, a bez tego znacznika
   * odpowiedź na „czy ten pracownik jest już u księgowej" wymagałaby otwarcia
   * ustawień integracji. Pusto, gdy najemca nie ma włączonych kadr — i to jest
   * poprawne: brak wiersza znaczy „nie wysyłany", a nie „błąd".
   */
  const [znacznikiKadr, setZnacznikiKadr] = useState<Map<string, WierszKadr>>(new Map());

  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterMode, setFilterMode] = useState<FilterMode>('active');

  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [editingWorker, setEditingWorker] = useState<ShopFloorWorker | null>(null);
  const [formData, setFormData] = useState<WorkerFormData>(initialFormData);
  const [isSaving, setIsSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [isLoadingEditor, setIsLoadingEditor] = useState(false);
  const [activeModalTab, setActiveModalTab] = useState<
    'basic' | 'communication' | 'role' | 'contact'
  >('basic');

  // Delete confirmation
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const canManageWorkers = hasRole(['owner', 'admin', 'manager']);
  // Uprawnienia w organizacji nadaje wyłącznie administrator (RPC 892 sprawdza to samo).
  const canSetOrgRole = hasRole(['owner', 'admin']);

  // ── Receptor: osoby i PIN-y (kontrakt „PIN z Receptora", migracja 758) ──────
  //
  // Wszystko poniżej jest NIEOBECNE, dopóki organizacja nie ma agenta, który
  // to umie (aktywny, w licencji, ≥ 0.5.0) i dopóki organ Receptora nie jest
  // wdrożony. Bez tego ekran działa dokładnie tak, jak przed tą zmianą.
  // Powiązanie i pobranie PIN-u zapisuje receptor-core, który wpuszcza wyłącznie
  // role administracyjne — dlatego sekcja i przyciski są dla `ROLE_ZARZADZAJACE`,
  // a nie dla całego `canManageWorkers` (kierownik dostałby same odmowy) —
  // i tylko w jego obrębie, bo formularz pracownika jest dla `canManageWorkers`.
  const orgId = currentOrganization?.organization_id;
  const adminReceptora = canManageWorkers && hasRole([...ROLE_ZARZADZAJACE]);
  const [agenciReceptora, setAgenciReceptora] = useState<AgenciDoOsob<AgentReceptora> | null>(null);
  /**
   * Świeże wiersze agentów do zdania „czekam na agenta” (plan 59, S10): żywość
   * i okres pulsu. Lista do formularza wczytuje się raz; ta odświeża się razem
   * ze stanem PIN-ów, dopóki któryś czeka — werdykt „offline” nie może pochodzić
   * sprzed wejścia na ekran.
   */
  const [agenciZywosc, setAgenciZywosc] = useState<Map<string, AgentReceptora>>(new Map());
  const receptorAktywny = (agenciReceptora?.zdolni.length ?? 0) > 0;
  const [stanyReceptora, setStanyReceptora] = useState<Map<string, StanReceptoraPracownika>>(
    new Map()
  );
  const [terazReceptora, setTerazReceptora] = useState(() => Date.now());
  const [pinyWToku, setPinyWToku] = useState<Set<string>>(new Set());
  const [bladyPinow, setBladyPinow] = useState<Map<string, string>>(new Map());
  const [wyborReceptora, setWyborReceptora] = useState<WyborOsobyReceptora | null>(null);
  const [pinRecznie, setPinRecznie] = useState(false);
  /** Pracownik zapisany, powiązanie nie — zostaje w formularzu z przyciskiem ponowienia. */
  const [bladPowiazania, setBladPowiazania] = useState<string | null>(null);
  /** Dodany przed chwilą BEZ PIN-u (miał przyjść z Receptora), a powiązanie padło. */
  const [zapisanyBezPinu, setZapisanyBezPinu] = useState(false);
  const [ponawiamPowiazanie, setPonawiamPowiazanie] = useState(false);

  const pinZRec = receptorAktywny && pinZReceptora(wyborReceptora?.osoba ?? null, pinRecznie);

  const odswiezStanReceptora = useCallback(async () => {
    if (!orgId) return;
    try {
      const [stany, agenci] = await Promise.all([
        stanReceptoraPracownikow(orgId),
        // Lista agentów jest tu wyłącznie do zdania o czekaniu — jej błąd nie
        // może zabrać kafelkom stanu PIN-ów, więc zostaje poprzednia.
        listaAgentow(orgId).catch(() => null),
      ]);
      setStanyReceptora(stany);
      if (agenci) setAgenciZywosc(new Map(agenci.map((a) => [a.id, a])));
      setTerazReceptora(Date.now());
    } catch (err) {
      // Brak stanu PIN-ów nie jest awarią kartoteki — kafelki zostają bez niego.
      console.warn(
        'Nie udało się odczytać powiązań z Receptorem:',
        err instanceof Error ? err.message : err
      );
    }
  }, [orgId]);

  useEffect(() => {
    if (!orgId || !receptorCoreDostepny()) {
      setAgenciReceptora(null);
      return;
    }
    let aktualne = true;
    void (async () => {
      try {
        const [agenci, licencja] = await Promise.all([
          listaAgentow(orgId),
          // Licencja, której nie da się sprawdzić, to NIE „jest licencja" —
          // wtedy nie proponujemy niczego, co usługa i tak odrzuci.
          stanLicencjiReceptora(orgId).catch(() => 'blad' as const),
        ]);
        if (!aktualne) return;
        const podzial = agenciDoOsob(agenci, licencja);
        setAgenciReceptora(podzial);
        if (podzial.zdolni.length > 0) await odswiezStanReceptora();
      } catch (err) {
        if (!aktualne) return;
        setAgenciReceptora(null);
        console.warn(
          'Nie udało się sprawdzić agentów Receptora:',
          err instanceof Error ? err.message : err
        );
      }
    })();
    return () => {
      aktualne = false;
    };
  }, [orgId, odswiezStanReceptora]);

  // Odświeżanie WYŁĄCZNIE, dopóki jakieś zadanie pin czeka (i nie przekroczyło
  // limitu) — stała pętla na ekranie, na którym nic się nie dzieje, to ruch
  // do bazy bez nowej informacji.
  const pinyCzekaja = useMemo(
    () =>
      [...stanyReceptora.values()].some((s) =>
        pinCzeka(opisPinuZReceptora(s.zadanie, terazReceptora))
      ),
    [stanyReceptora, terazReceptora]
  );
  useEffect(() => {
    if (!receptorAktywny || !pinyCzekaja) return;
    const id = setInterval(() => void odswiezStanReceptora(), ODSWIEZANIE_STANU_PINOW_MS);
    return () => clearInterval(id);
  }, [receptorAktywny, pinyCzekaja, odswiezStanReceptora]);

  const nazwyPracownikow = useMemo(
    () => new Map(rows.map((r) => [r.worker.id, workerFullName(r.worker)])),
    [rows]
  );

  /** Kto INNY ma już tę osobę z Receptora przy tym agencie — lista blokuje ją z nazwiskiem. */
  const zajetaPrzez = (agentId: string, idUser: number): string | null => {
    for (const [workerId, s] of stanyReceptora) {
      if (workerId === editingWorker?.id) continue;
      if (s.powiazanie.agent_id === agentId && s.powiazanie.id_user === idUser) {
        return nazwyPracownikow.get(workerId) ?? 'inny pracownik';
      }
    }
    return null;
  };

  const wybierzOsobeReceptora = (wybor: WyborOsobyReceptora | null) => {
    setWyborReceptora(wybor);
    setPinRecznie(false);
    if (!wybor) return;
    // Wybór uzupełnia WYŁĄCZNIE puste pola — wpisanego imienia nie nadpisuje.
    setFormData((prev) => ({
      ...prev,
      first_name: prev.first_name.trim() ? prev.first_name : (wybor.osoba.imie ?? ''),
      last_name: prev.last_name.trim() ? prev.last_name : (wybor.osoba.nazwisko ?? ''),
    }));
  };

  const ustawPinRecznie = () => {
    setPinRecznie(true);
    setFormData((prev) => (prev.pin ? prev : { ...prev, pin: generateWorkerPin() }));
  };

  const resetReceptoraFormularza = () => {
    setWyborReceptora(null);
    setPinRecznie(false);
    setBladPowiazania(null);
    setZapisanyBezPinu(false);
  };

  /**
   * Powiązanie wybranej osoby z JUŻ zapisanym pracownikiem. `false` = nie
   * wyszło; formularz zostaje otwarty z powodem i przyciskiem ponowienia.
   */
  const powiazWybranaOsobe = async (workerId: string): Promise<boolean> => {
    if (!orgId || !wyborReceptora) return false;
    try {
      await powiazZReceptorem(orgId, workerId, {
        agent_id: wyborReceptora.agentId,
        id_user: wyborReceptora.osoba.id_user,
        pobierz_pin: pinZReceptora(wyborReceptora.osoba, pinRecznie),
      });
      setBladPowiazania(null);
      setZapisanyBezPinu(false);
      void odswiezStanReceptora();
      return true;
    } catch (err) {
      setBladPowiazania(
        `Pracownik jest zapisany, ale powiązanie z Receptorem nie powiodło się: ${komunikatBleduReceptora(err)}`
      );
      return false;
    }
  };

  const ponowPowiazanie = async () => {
    if (!editingWorker) return;
    setPonawiamPowiazanie(true);
    const ok = await powiazWybranaOsobe(editingWorker.id);
    setPonawiamPowiazanie(false);
    if (ok) {
      await fetchWorkers();
      closeModal();
    }
  };

  const pobierzPinPracownika = async (workerId: string) => {
    if (!orgId) return;
    setPinyWToku((prev) => new Set(prev).add(workerId));
    setBladyPinow((prev) => {
      const nast = new Map(prev);
      nast.delete(workerId);
      return nast;
    });
    try {
      await pobierzPinZReceptora(orgId, workerId);
      await odswiezStanReceptora();
    } catch (err) {
      setBladyPinow((prev) => new Map(prev).set(workerId, komunikatBleduReceptora(err)));
    } finally {
      setPinyWToku((prev) => {
        const nast = new Set(prev);
        nast.delete(workerId);
        return nast;
      });
    }
  };

  const getLanguageInfo = (code: string) => {
    return SUPPORTED_LANGUAGES.find((l) => l.code === code) || SUPPORTED_LANGUAGES[0];
  };

  const getRoleInfo = (role: string) => {
    return WORKER_ROLES.find((r) => r.value === role) || WORKER_ROLES[1];
  };

  const openAddModal = (preselectedModule?: string) => {
    setEditingWorker(null);
    // PIN nadaje system — administrator nie musi go wymyślać. Pole zostaje
    // edytowalne, a „Generuj" losuje kolejny.
    setFormData({
      ...initialFormData,
      pin: generateWorkerPin(),
      ...(preselectedModule && WORKER_MODULE_SLUGS.includes(preselectedModule)
        ? { module_access: [preselectedModule] }
        : {}),
    });
    setFormError(null);
    resetReceptoraFormularza();
    setActiveModalTab('basic');
    setShowModal(true);
  };

  /**
   * Formularz edycji potrzebuje kolumn, których LISTA celowo nie pobiera
   * (forma zatrudnienia, wymiar etatu, urlopy). Zamiast wozić je dla całej
   * załogi przy każdym wejściu na ekran, dociągamy pełny wiersz dla JEDNEGO
   * pracownika w chwili otwarcia modala.
   *
   * `nowyPin` — „Wygeneruj PIN" z kafelka, gdy PIN z Receptora nie przeszedł:
   * edycja otwiera się z wylosowanym PIN-em gotowym do zapisu.
   */
  const openEditModal = async (workerId: string, opcje?: { nowyPin?: boolean }) => {
    if (!currentOrganization) return;
    setIsLoadingEditor(true);
    setError(null);

    const { data, error: fetchError } = await supabase
      .from('shop_floor_workers')
      .select('*')
      .eq('organization_id', currentOrganization.organization_id)
      .eq('id', workerId)
      .maybeSingle();

    setIsLoadingEditor(false);

    if (fetchError || !data) {
      console.error('Error loading worker for edit:', fetchError);
      setError('Nie udało się wczytać danych pracownika');
      return;
    }

    const worker = data as ShopFloorWorker;
    setEditingWorker(worker);
    setFormData({
      first_name: worker.first_name,
      last_name: worker.last_name,
      pin: opcje?.nowyPin ? generateWorkerPin() : '',
      position: worker.position || '',
      email: worker.email || '',
      phone: worker.phone || '',
      module_access: workerModuleAccess(worker.preferences, WORKER_MODULE_SLUGS),
      preferred_language: worker.preferred_language || 'pl',
      experience_level: worker.experience_level || 'novice',
      worker_role: worker.worker_role || 'worker',
      communication_style: worker.communication_style || 'simple',
      // Employment fields
      employment_type: worker.employment_type || 'employment',
      contract_type: worker.contract_type || 'indefinite',
      can_work_weekends: worker.can_work_weekends || false,
      can_work_holidays: worker.can_work_holidays || false,
      available_all_departments: worker.available_all_departments ?? true,
      subject_to_labor_code: worker.subject_to_labor_code ?? true,
      hire_date: worker.hire_date || '',
      weekly_hours: worker.weekly_hours || 40,
      annual_leave_days: worker.annual_leave_days || 26,
    });
    setFormError(null);
    resetReceptoraFormularza();
    setActiveModalTab('basic');
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingWorker(null);
    setFormData(initialFormData);
    setFormError(null);
    resetReceptoraFormularza();
  };

  /**
   * Przypisanie z poziomu plakietki na kafelku — bez otwierania modala.
   * Idzie przez RPC, więc zmienia WYŁĄCZNIE `module_access` i nie dotyka
   * pozostałych kluczy `preferences`.
   */
  const handleChipToggle = async (workerId: string, moduleSlug: string, enabled: boolean) => {
    setError(null);
    try {
      await setWorkerModuleAccess(workerId, [moduleSlug], enabled ? 'add' : 'remove');
      await fetchWorkers();
    } catch (err) {
      console.error('Error toggling module access:', err);
      setError('Nie udało się zmienić przypisania do modułu');
    }
  };

  const handleModuleToggle = (moduleId: string) => {
    setFormData((prev) => ({
      ...prev,
      module_access: prev.module_access.includes(moduleId)
        ? prev.module_access.filter((m) => m !== moduleId)
        : [...prev.module_access, moduleId],
    }));
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentOrganization) return;

    // Validation
    if (!formData.first_name.trim() || !formData.last_name.trim()) {
      setFormError('Imię i nazwisko są wymagane');
      return;
    }

    // PIN z Receptora przychodzi PO zapisie (agent → receptor-core → baza),
    // więc formularz go nie wymaga ani nie sprawdza — pole PIN jest wtedy ukryte.
    if (!editingWorker && !formData.pin && !pinZRec) {
      setFormError('PIN jest wymagany dla nowego pracownika');
      return;
    }

    // Reguła jedna dla całego systemu — patrz WORKER_PIN_PATTERN w @insolitum/types.
    if (!pinZRec && formData.pin && !isValidWorkerPin(formData.pin)) {
      setFormError(WORKER_PIN_HINT);
      return;
    }

    if (formData.module_access.length === 0) {
      setFormError('Pracownik musi mieć dostęp do co najmniej jednego modułu');
      return;
    }

    setIsSaving(true);
    setFormError(null);

    try {
      // `preferences` NIE jedzie w tym obiekcie przy edycji: PostgREST podmienia
      // cały blob, więc równoległy zapis z innego ekranu skasowałby klucze,
      // których ten formularz nawet nie pokazuje (`language`,
      // `notifications_enabled`). Przypisania idą osobno, przez RPC z
      // `jsonb_set`. Przy INSERCIE blobu nie ma z czym kolidować, więc tam
      // zapisujemy je wprost.
      const workerData = {
        organization_id: currentOrganization.organization_id,
        first_name: formData.first_name.trim(),
        last_name: formData.last_name.trim(),
        position: formData.position.trim() || null,
        email: formData.email.trim() || null,
        phone: formData.phone.trim() || null,
        preferred_language: formData.preferred_language,
        experience_level: formData.experience_level,
        worker_role: formData.worker_role,
        communication_style: formData.communication_style,
        // Employment fields
        employment_type: formData.employment_type,
        contract_type: formData.contract_type,
        can_work_weekends: formData.can_work_weekends,
        can_work_holidays: formData.can_work_holidays,
        available_all_departments: formData.available_all_departments,
        subject_to_labor_code: formData.subject_to_labor_code,
        hire_date: formData.hire_date || null,
        weekly_hours: formData.weekly_hours,
        annual_leave_days: formData.annual_leave_days,
        ...(formData.pin && !pinZRec ? { pin: formData.pin } : {}),
      };

      const nowyWiersz = {
        ...workerData,
        preferences: { module_access: formData.module_access } satisfies WorkerPreferences,
        is_active: true,
        total_points: 0,
        current_level: 1,
        performance_score: 50,
      };

      // Wybór osoby z Receptora jest możliwy tylko przy aktywnym Receptorze.
      const wybor = receptorAktywny ? wyborReceptora : null;
      let zapisanyId: string | null = null;

      if (editingWorker) {
        const { error } = await supabase
          .from('shop_floor_workers')
          .update(workerData)
          .eq('id', editingWorker.id);

        if (error) throw error;
        await setWorkerModuleAccess(editingWorker.id, formData.module_access, 'set');
        zapisanyId = editingWorker.id;
        // PIN wpisany ręcznie właśnie zapisano — „nie ma jeszcze PIN-u" przestaje być prawdą.
        if (formData.pin && !pinZRec) setZapisanyBezPinu(false);
      } else if (wybor) {
        // Powiązanie potrzebuje id nowego wiersza — stąd `select('id')`.
        // Bez wyboru z Receptora INSERT zostaje dokładnie taki, jak był.
        const { data, error } = await supabase
          .from('shop_floor_workers')
          .insert([nowyWiersz])
          .select('id')
          .single();

        if (error) throw error;
        zapisanyId = (data as { id: string }).id;
      } else {
        const { error } = await supabase.from('shop_floor_workers').insert([nowyWiersz]);

        if (error) throw error;
      }

      if (wybor && zapisanyId) {
        const powiazano = await powiazWybranaOsobe(zapisanyId);
        if (!powiazano) {
          await fetchWorkers();
          if (!editingWorker) {
            // Pracownik JUŻ jest w kartotece — formularz przechodzi w jego
            // edycję, żeby ponowny zapis nie założył drugiego wiersza.
            const { data: wiersz } = await supabase
              .from('shop_floor_workers')
              .select('*')
              .eq('organization_id', currentOrganization.organization_id)
              .eq('id', zapisanyId)
              .maybeSingle();
            if (!wiersz) {
              closeModal();
              setError(
                `Pracownik ${workerData.first_name} ${workerData.last_name} został dodany, ale nie powiązano go z Receptorem — otwórz jego edycję i wybierz osobę ponownie.`
              );
              return;
            }
            setEditingWorker(wiersz as ShopFloorWorker);
            setZapisanyBezPinu(pinZRec);
            setFormData((prev) => ({ ...prev, pin: '' }));
          }
          return;
        }
      }

      await fetchWorkers();
      closeModal();
    } catch (err) {
      console.error('Error saving worker:', err);
      // Unikalność i format PIN-u rozstrzyga wyzwalacz `shop_floor_workers_pin_bi`
      // (269) — RAISE EXCEPTION, czyli P0001 z nazwą w `message`, nie 23505.
      const message = (err as { message?: string } | null)?.message ?? '';
      if (message.includes('pin_taken')) {
        setFormData((prev) => ({ ...prev, pin: generateWorkerPin() }));
        setFormError(
          'Ten PIN ma już inny pracownik. Wylosowano nowy — sprawdź go i zapisz ponownie.'
        );
      } else if (message.includes('invalid_pin_format')) {
        setFormError(WORKER_PIN_HINT);
      } else {
        setFormError('Nie udało się zapisać pracownika');
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleActive = async (workerId: string, isActive: boolean) => {
    try {
      const { error } = await supabase
        .from('shop_floor_workers')
        .update({ is_active: !isActive })
        .eq('id', workerId);

      if (error) throw error;
      await fetchWorkers();
    } catch (err) {
      console.error('Error toggling worker status:', err);
      setError('Nie udało się zmienić statusu pracownika');
    }
  };

  const handleDelete = async (workerId: string) => {
    setIsDeleting(true);
    try {
      const { error } = await supabase.from('shop_floor_workers').delete().eq('id', workerId);

      if (error) throw error;
      await fetchWorkers();
      setShowDeleteConfirm(null);
    } catch (err) {
      console.error('Error deleting worker:', err);
      setError('Nie udało się usunąć pracownika');
    } finally {
      setIsDeleting(false);
    }
  };

  useEffect(() => {
    // `organization_id`, nie `id` — `currentOrganization.id` to id CZŁONKOSTWA
    // i z nim zapytanie nie zwracało żadnego wiersza, więc znacznika nie było
    // nigdy. `system = 'saldeo'`, bo od 758 ta sama mapa trzyma też powiązania
    // z Receptorem, a znacznik mówi wyłącznie o kadrach.
    if (!orgId) return;
    let aktualne = true;
    void (async () => {
      const { data, error: bladOdczytu } = await supabase
        .from('shop_floor_worker_external_ids')
        .select('worker_id,external_id,acronym,last_status,last_error,synced_at,updated_at')
        .eq('organization_id', orgId)
        .eq('system', 'saldeo');
      if (!aktualne) return;
      if (bladOdczytu) {
        // Brak znacznika to nie jest awaria kartoteki — kartoteka ma działać
        // tak samo, gdy integracja kadrowa jest wyłączona albo niedostępna.
        console.warn('Nie udało się odczytać znaczników kadrowych:', bladOdczytu.message);
        return;
      }
      // Po 713 pracownik może mieć wiersze z kilku firm systemu zewnętrznego —
      // znacznik pokazuje NAJŚWIEŻSZY, a nie ten, który baza oddała ostatni.
      setZnacznikiKadr(najnowszyNaPracownika((data ?? []) as WierszKadr[]));
    })();
    return () => {
      aktualne = false;
    };
  }, [orgId]);

  // Otwarcie z wnętrza modułu: `?new=1&module=<slug>` znaczy „kliknąłem Nowy
  // pracownik w organie X". Bez tego moduł potrafił tylko wyrzucić kadrową na
  // nieprzefiltrowaną listę bez śladu po tym, po co tam weszła.
  useEffect(() => {
    if (searchParams.get('new') !== '1' || !canManageWorkers) return;
    openAddModal(searchParams.get('module') ?? undefined);
    const next = new URLSearchParams(searchParams);
    next.delete('new');
    next.delete('module');
    setSearchParams(next, { replace: true });
    // Zależności celowo tylko od parametrów i uprawnień: `openAddModal` i
    // `setSearchParams` są stabilne w obrębie renderu, a dopisanie ich tutaj
    // otwierałoby modal ponownie przy każdej zmianie stanu formularza.
  }, [searchParams, canManageWorkers, setSearchParams]);

  /**
   * Filtrowanie zawsze po stronie WIDOKU, nigdy w zapytaniu. Zapytanie musi
   * zwracać całą załogę, bo nazwiska rozwiązujemy z tej samej tablicy —
   * odsianie kogoś w SQL-u zamienia go w „nieznanego pracownika" wszędzie
   * tam, gdzie jego identyfikator jeszcze występuje.
   */
  const visibleRows = useMemo<CatalogRow[]>(() => {
    return rows.filter((row) => {
      if (!matchesWorkerSearch(row.worker, searchQuery)) return false;
      if (filterMode === 'active' && !row.worker.is_active) return false;
      if (filterMode === 'gaps') {
        // „Luki i sygnały" to dwie sprawy naraz: przypisany, a nigdzie nie
        // mierzony (wdrożenie stanęło), albo mierzony bez przypisania (organ
        // ma jego dane, ale nie widać go na swojej liście).
        const measured = measuredModuleCount(row.modules);
        const orphans = orphanModules(row.modules);
        if (orphans.length === 0 && measured > 0) return false;
        if (!row.worker.is_active) return false;
      }
      return true;
    });
  }, [rows, searchQuery, filterMode]);

  const counts = useMemo(() => {
    const active = rows.filter((r) => r.worker.is_active).length;
    const gaps = rows.filter(
      (r) =>
        r.worker.is_active &&
        (orphanModules(r.modules).length > 0 || measuredModuleCount(r.modules) === 0)
    ).length;
    return { total: rows.length, active, gaps };
  }, [rows]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-aurora-brand animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* ── Nagłówek ── */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div>
          <h1 className="pagehead__title flex items-center gap-2">
            <HardHat className="w-6 h-6 text-orange-400" />
            Pracownicy produkcyjni
          </h1>
          <p className="text-gray-400 mt-1">
            Kartoteki załogi z wynikami ze wszystkich organów, w których jest mierzona
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {/* Okno czasowe dotyczy WYŁĄCZNIE wyników; kartoteki są zawsze aktualne. */}
          <div className="flex items-center gap-1 bg-gray-800 border border-gray-700 rounded-ids-md p-1">
            {WORKER_WINDOW_OPTIONS.map((days) => (
              <button
                key={days}
                onClick={() => setWindowDays(days)}
                className={`px-2.5 py-1 rounded-ids-sm text-xs font-medium transition-colors ${
                  windowDays === days
                    ? 'bg-orange-600 text-aurora-on-accent'
                    : 'text-gray-400 hover:text-aurora-text hover:bg-gray-700'
                }`}
              >
                {WORKER_WINDOW_LABELS[days]}
              </button>
            ))}
          </div>
          {canManageWorkers && (
            <button
              onClick={() => openAddModal()}
              className="flex items-center gap-2 px-4 py-2 bg-orange-600 hover:bg-orange-700 text-aurora-on-accent rounded-ids-md transition-colors"
            >
              <Plus className="w-5 h-5" />
              Dodaj pracownika
            </button>
          )}
        </div>
      </div>

      {/* ── Filtry ── */}
      <div className="flex flex-wrap items-center gap-2">
        {(Object.keys(FILTER_LABELS) as FilterMode[]).map((mode) => {
          const count =
            mode === 'all' ? counts.total : mode === 'active' ? counts.active : counts.gaps;
          return (
            <button
              key={mode}
              onClick={() => setFilterMode(mode)}
              className={`px-3 py-1.5 rounded-ids-md text-sm font-medium border transition-colors ${
                filterMode === mode
                  ? mode === 'gaps'
                    ? 'bg-amber-500/20 text-amber-300 border-amber-500/40'
                    : 'bg-orange-600 text-aurora-on-accent border-orange-600'
                  : 'bg-gray-800 text-gray-400 border-gray-700 hover:text-aurora-text'
              }`}
              title={
                mode === 'gaps'
                  ? 'Pracownicy, którzy nigdzie nie są mierzeni albo mają dane w organie, do którego nie są przypisani'
                  : undefined
              }
            >
              {FILTER_LABELS[mode]}
              <span className="ml-1.5 opacity-70 tabular-nums">{count}</span>
            </button>
          );
        })}
      </div>

      {/* ── Błędy ── */}
      {(error || catalogError) && (
        <div className="flex items-center gap-2 p-4 bg-red-500/20 border border-red-500/30 rounded-ids-md text-red-400">
          <AlertCircle className="w-5 h-5" />
          {error || catalogError}
          <button onClick={() => setError(null)} className="ml-auto">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {statsUnavailable && (
        <div className="flex items-center gap-2 p-3 bg-amber-500/10 border border-amber-500/25 rounded-ids-md text-xs text-amber-300">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          Nie udało się policzyć wyników w modułach — kartoteki poniżej działają normalnie, brakuje
          tylko liczb na plakietkach.
        </div>
      )}

      {/* ── Szukaj ── */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
        <input
          type="text"
          placeholder="Szukaj pracownika (imię, nazwisko, e-mail, stanowisko)..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="input"
        />
      </div>

      {/* ── Siatka kartotek ── */}
      {visibleRows.length === 0 ? (
        <div className="bg-gray-800 rounded-ids-md p-8 text-center">
          <HardHat className="w-12 h-12 text-gray-600 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-aurora-text mb-2">
            {searchQuery || filterMode !== 'all'
              ? 'Nie znaleziono pracowników'
              : 'Brak pracowników'}
          </h3>
          <p className="text-gray-400 mb-4">
            {filterMode === 'gaps'
              ? 'Nikt nie ma luk w przypisaniach — każdy aktywny pracownik jest mierzony w organie, do którego należy.'
              : searchQuery
                ? 'Spróbuj zmienić kryteria wyszukiwania'
                : 'Dodaj pierwszego pracownika, aby zacząć'}
          </p>
          {!searchQuery && filterMode === 'all' && canManageWorkers && (
            <button
              onClick={() => openAddModal()}
              className="inline-flex items-center gap-2 px-4 py-2 bg-orange-600 hover:bg-orange-700 text-aurora-on-accent rounded-ids-md transition-colors"
            >
              <Plus className="w-5 h-5" />
              Dodaj pracownika
            </button>
          )}
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {visibleRows.map((row) => {
            const worker = row.worker;
            const languageInfo = getLanguageInfo(worker.preferred_language ?? 'pl');
            const roleInfo = getRoleInfo(worker.worker_role ?? 'worker');
            const measured = measuredModuleCount(row.modules);
            const orphans = orphanModules(row.modules);
            const znacznikKadrowy = znacznikiKadr.get(worker.id);
            const znacznikKadrowyWymagaUwagi = znacznikKadrowy
              ? wymagajaUwagi([znacznikKadrowy]).length > 0
              : false;
            const stanReceptora = receptorAktywny ? stanyReceptora.get(worker.id) : undefined;

            return (
              <div
                key={worker.id}
                onClick={() => navigate(`/admin/workers/${worker.id}`)}
                className={`bg-gray-800 rounded-ids-md border cursor-pointer transition-colors hover:border-gray-600 ${
                  worker.is_active ? 'border-gray-700' : 'border-red-500/30 opacity-75'
                }`}
              >
                {/* Tożsamość */}
                <div className="p-4 border-b border-gray-700">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden">
                        {worker.avatar_url ? (
                          <img
                            src={worker.avatar_url}
                            alt=""
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <span className="text-lg font-bold text-aurora-text">
                            {workerInitials(worker)}
                          </span>
                        )}
                      </div>
                      <div className="min-w-0">
                        <h3 className="font-medium text-aurora-text truncate">
                          {workerFullName(worker)}
                        </h3>
                        <div className="flex items-center gap-2">
                          <span className={`text-sm ${roleInfo.color}`}>{roleInfo.label}</span>
                          <span className="text-gray-500">·</span>
                          <span className="text-sm text-gray-400" title={languageInfo.name}>
                            {languageInfo.flag}
                          </span>
                          {znacznikKadrowy && (
                            <span
                              title={`Saldeo Kadry: ${opiszStanTozsamosci(znacznikKadrowy.last_status)}`}
                              className={`text-xs px-1.5 py-0.5 rounded ${
                                znacznikKadrowyWymagaUwagi
                                  ? 'text-amber-300 bg-amber-500/10'
                                  : 'text-emerald-300 bg-emerald-500/10'
                              }`}
                            >
                              {znacznikKadrowyWymagaUwagi ? '⚠' : '✓'} Saldeo
                            </span>
                          )}
                          {stanReceptora && (
                            <span
                              title={`Powiązany z osobą${
                                stanReceptora.powiazanie.id_user !== null
                                  ? ` nr ${stanReceptora.powiazanie.id_user}`
                                  : ''
                              } w Receptorze`}
                              className="text-xs px-1.5 py-0.5 rounded text-sky-300 bg-sky-500/10"
                            >
                              Receptor
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    {canManageWorkers && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          void openEditModal(worker.id);
                        }}
                        disabled={isLoadingEditor}
                        title="Edytuj dane pracownika"
                        className="p-1.5 hover:bg-gray-700 rounded-ids-md transition-colors text-gray-400 flex-shrink-0 disabled:opacity-50"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>

                {/* Gdzie jest mierzony — to jest sedno kartoteki: plakietka niesie
                    metrykę wiodącą organu, a kliknięcie przypisuje/odpina. */}
                <div className="p-4 border-b border-gray-700">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-[11px] uppercase tracking-wider text-gray-500 flex items-center gap-1.5">
                      <Layers className="w-3 h-3" />
                      Mierzony w {measured} z {row.modules.length} organów
                    </p>
                    {isLoadingStats && <Loader2 className="w-3 h-3 text-gray-600 animate-spin" />}
                  </div>
                  <ModuleAccessChips
                    scorecards={row.modules}
                    canManage={canManageWorkers}
                    onToggle={(slug, enabled) => handleChipToggle(worker.id, slug, enabled)}
                  />
                  {orphans.length > 0 && (
                    <p className="text-[11px] text-amber-400/90 mt-2">
                      Ma dane w organie bez przypisania — przez to nie widać go na jego liście.
                    </p>
                  )}
                </div>

                {/* Kontakt */}
                <div className="p-4 space-y-2 text-sm">
                  {worker.position && (
                    <div className="flex items-center gap-2 text-gray-400">
                      <HardHat className="w-4 h-4 flex-shrink-0" />
                      <span className="truncate">{worker.position}</span>
                    </div>
                  )}
                  {worker.email && (
                    <div className="flex items-center gap-2 text-gray-400">
                      <Mail className="w-4 h-4 flex-shrink-0" />
                      <span className="truncate">{worker.email}</span>
                    </div>
                  )}
                  {worker.phone && (
                    <div className="flex items-center gap-2 text-gray-400">
                      <Phone className="w-4 h-4 flex-shrink-0" />
                      <span>{worker.phone}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-gray-400">
                    <KeyRound className="w-4 h-4 flex-shrink-0" />
                    <span className="font-mono">****</span>
                    <span className="text-xs text-gray-500">(PIN ukryty)</span>
                  </div>
                  {stanReceptora && (
                    <StanPinuReceptora
                      opis={opisPinuZReceptora(
                        stanReceptora.zadanie,
                        terazReceptora,
                        agenciZywosc.get(stanReceptora.powiazanie.agent_id) ?? null
                      )}
                      idUser={stanReceptora.powiazanie.id_user}
                      nazwaPracownika={workerFullName(worker)}
                      mozeZarzadzac={adminReceptora}
                      wToku={pinyWToku.has(worker.id)}
                      blad={bladyPinow.get(worker.id) ?? null}
                      onPobierz={() => void pobierzPinPracownika(worker.id)}
                      onWygeneruj={() => void openEditModal(worker.id, { nowyPin: true })}
                      onSprawdz={() => void odswiezStanReceptora()}
                    />
                  )}
                  <div className="flex items-center gap-2 text-gray-400">
                    <Clock className="w-4 h-4 flex-shrink-0" />
                    <span>Ostatnie logowanie: {formatRelativeDate(worker.last_login_at)}</span>
                  </div>
                </div>

                {/* Akcje */}
                <div className="flex items-center justify-between p-4 pt-3 border-t border-gray-700">
                  {canManageWorkers ? (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        void handleToggleActive(worker.id, worker.is_active);
                      }}
                      className={`flex items-center gap-1.5 text-sm ${
                        worker.is_active
                          ? 'text-green-400 hover:text-green-300'
                          : 'text-red-400 hover:text-red-300'
                      }`}
                    >
                      {worker.is_active ? (
                        <>
                          <CheckCircle className="w-4 h-4" />
                          Aktywny
                        </>
                      ) : (
                        <>
                          <XCircle className="w-4 h-4" />
                          Nieaktywny
                        </>
                      )}
                    </button>
                  ) : (
                    <span
                      className={`flex items-center gap-1.5 text-sm ${
                        worker.is_active ? 'text-green-400' : 'text-red-400'
                      }`}
                    >
                      {worker.is_active ? (
                        <CheckCircle className="w-4 h-4" />
                      ) : (
                        <XCircle className="w-4 h-4" />
                      )}
                      {worker.is_active ? 'Aktywny' : 'Nieaktywny'}
                    </span>
                  )}

                  <div className="flex items-center gap-1">
                    <span className="flex items-center gap-1 text-xs text-gray-500">
                      Kartoteka
                      <ChevronRight className="w-3.5 h-3.5" />
                    </span>
                    {canManageWorkers && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowDeleteConfirm(worker.id);
                        }}
                        className="p-1.5 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded-ids-md transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>

                {/* Potwierdzenie usunięcia */}
                {showDeleteConfirm === worker.id && (
                  <div className="p-4 pt-0" onClick={(e) => e.stopPropagation()}>
                    <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-ids-md">
                      <p className="text-sm text-red-400 mb-2">
                        Czy na pewno chcesz usunąć tego pracownika?
                      </p>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleDelete(worker.id)}
                          disabled={isDeleting}
                          className="btn btn--danger btn--sm"
                        >
                          {isDeleting ? 'Usuwanie...' : 'Usuń'}
                        </button>
                        <button
                          onClick={() => setShowDeleteConfirm(null)}
                          className="btn btn--secondary btn--sm"
                        >
                          Anuluj
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-aurora-scrim backdrop-blur-[3px]"
            onClick={closeModal}
          />
          <div className="relative bg-aurora-surface rounded-ids-xl shadow-elev-4 max-w-5xl w-full border border-aurora-border max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-aurora-border">
              <div className="flex items-center gap-4">
                <span className="avatar w-14 h-14 text-xl rounded-ids-lg">
                  {formData.first_name || formData.last_name ? (
                    <span className="font-bold">
                      {formData.first_name?.[0] || ''}
                      {formData.last_name?.[0] || ''}
                    </span>
                  ) : (
                    <HardHat className="w-7 h-7 text-aurora-on-accent" />
                  )}
                </span>
                <div>
                  <h2 className="display text-xl font-bold text-aurora-text">
                    {editingWorker ? 'Edytuj pracownika' : 'Dodaj nowego pracownika'}
                  </h2>
                  <p className="text-sm text-aurora-muted">
                    {editingWorker
                      ? `${editingWorker.first_name} ${editingWorker.last_name}`
                      : 'Wypełnij dane pracownika produkcyjnego'}
                  </p>
                </div>
              </div>
              <button onClick={closeModal} className="iconbtn">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Tab Navigation */}
            <div className="tabs px-6 overflow-x-auto">
              {[
                { id: 'basic' as const, label: 'Podstawowe', icon: HardHat },
                { id: 'communication' as const, label: 'Komunikacja', icon: Globe },
                { id: 'role' as const, label: 'Rola i moduły', icon: Package },
                { id: 'contact' as const, label: 'Zatrudnienie', icon: Calendar },
              ].map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setActiveModalTab(tab.id)}
                    aria-selected={activeModalTab === tab.id}
                    className="flex shrink-0 items-center gap-2 whitespace-nowrap"
                  >
                    <Icon className="w-4 h-4" />
                    {tab.label}
                  </button>
                );
              })}
            </div>

            {/* Modal Body - Scrollable */}
            <div className="flex-1 overflow-y-auto p-6">
              <form id="worker-form" onSubmit={handleSave}>
                {formError && (
                  <div className="bg-red-500/10 border border-red-500/20 rounded-ids-md p-4 flex items-start gap-3 mb-6">
                    <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-red-400">{formError}</p>
                  </div>
                )}

                {bladPowiazania && (
                  <div
                    className="bg-amber-500/10 border border-amber-500/25 rounded-ids-md p-4 flex items-start gap-3 mb-6"
                    role="alert"
                  >
                    <AlertCircle className="w-5 h-5 text-amber-300 flex-shrink-0 mt-0.5" />
                    <div className="space-y-2">
                      <p className="text-sm text-amber-200">{bladPowiazania}</p>
                      {zapisanyBezPinu && (
                        <p className="text-sm text-amber-200">
                          Pracownik nie ma jeszcze PIN-u — spróbuj powiązać ponownie albo ustaw PIN
                          ręcznie i zapisz zmiany.
                        </p>
                      )}
                      <button
                        type="button"
                        onClick={() => void ponowPowiazanie()}
                        disabled={ponawiamPowiazanie || isSaving || !wyborReceptora}
                        className="btn btn--secondary btn--sm inline-flex items-center gap-2"
                      >
                        {ponawiamPowiazanie ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <RefreshCw className="w-4 h-4" />
                        )}
                        Spróbuj ponownie powiązać
                      </button>
                    </div>
                  </div>
                )}

                {/* Tab: Podstawowe */}
                {activeModalTab === 'basic' && (
                  <div className="space-y-6 max-w-2xl mx-auto">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="field__label block mb-1.5">
                          Imię <span className="text-orange-400">*</span>
                        </label>
                        <input
                          type="text"
                          value={formData.first_name}
                          onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                          required
                          className="input"
                        />
                      </div>
                      <div>
                        <label className="field__label block mb-1.5">
                          Nazwisko <span className="text-orange-400">*</span>
                        </label>
                        <input
                          type="text"
                          value={formData.last_name}
                          onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                          required
                          className="input"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="field__label block mb-1.5">Stanowisko</label>
                      <input
                        type="text"
                        value={formData.position}
                        onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                        placeholder="np. Piekarz, Cukiernik, Operator"
                        className="input"
                      />
                    </div>

                    {adminReceptora && receptorAktywny && orgId && agenciReceptora && (
                      <SekcjaPowiazaniaReceptora
                        organizationId={orgId}
                        agenci={agenciReceptora.zdolni}
                        wybor={wyborReceptora}
                        onWybor={wybierzOsobeReceptora}
                        obecne={
                          editingWorker
                            ? (stanyReceptora.get(editingWorker.id)?.powiazanie ?? null)
                            : null
                        }
                        zajetaPrzez={zajetaPrzez}
                        zablokowane={isSaving || ponawiamPowiazanie}
                      />
                    )}
                    {adminReceptora &&
                      !receptorAktywny &&
                      (agenciReceptora?.zaStarzy.length ?? 0) > 0 && (
                        <p className="text-xs text-aurora-subtle pt-4 border-t border-aurora-border">
                          {zdanieAgentaZaStarego(agenciReceptora!.zaStarzy[0])}
                        </p>
                      )}

                    <div>
                      <label className="field__label block mb-1.5">
                        PIN {!editingWorker && <span className="text-orange-400">*</span>}
                      </label>
                      {pinZRec ? (
                        <div
                          className="rounded-ids-md border border-aurora-border bg-aurora-bg-subtle p-3"
                          role="status"
                        >
                          <p className="text-sm text-aurora-text flex items-center gap-2">
                            <Download className="w-4 h-4 text-aurora-brand flex-shrink-0" />
                            PIN zostanie pobrany z Receptora po zapisie
                          </p>
                          <p className="text-xs text-aurora-subtle mt-1">
                            Agent przekaże go wprost do kartoteki — nie pojawi się na ekranie. Jeśli
                            okaże się zajęty, kafelek pracownika powie dlaczego i pozwoli
                            wygenerować nowy.
                          </p>
                          <button
                            type="button"
                            onClick={ustawPinRecznie}
                            disabled={isSaving}
                            className="btn btn--ghost btn--sm mt-2"
                          >
                            Ustaw PIN ręcznie
                          </button>
                        </div>
                      ) : (
                        <>
                          <div className="flex gap-2">
                            <input
                              type="text"
                              inputMode="numeric"
                              autoComplete="off"
                              value={formData.pin}
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  pin: e.target.value
                                    .replace(/\D/g, '')
                                    .slice(0, WORKER_PIN_LENGTH),
                                })
                              }
                              required={!editingWorker}
                              placeholder={
                                editingWorker
                                  ? 'Pozostaw puste, aby nie zmieniać'
                                  : WORKER_PIN_FORMAT_LABEL
                              }
                              maxLength={WORKER_PIN_LENGTH}
                              pattern={`\\d{${WORKER_PIN_LENGTH}}`}
                              className="input mono flex-1"
                            />
                            <button
                              type="button"
                              onClick={() =>
                                setFormData((prev) => ({ ...prev, pin: generateWorkerPin() }))
                              }
                              className="btn btn--secondary inline-flex items-center gap-2 shrink-0"
                              title="Wylosuj nowy PIN"
                            >
                              <RefreshCw className="w-4 h-4" />
                              Generuj PIN
                            </button>
                          </div>
                          <p className="text-xs text-aurora-subtle mt-1.5">
                            PIN służy do logowania w aplikacjach mobilnych.{' '}
                            {formData.pin
                              ? 'Przekaż go pracownikowi — po zapisie system przechowuje tylko jego skrót i nie pokaże go ponownie.'
                              : null}
                          </p>
                          {receptorAktywny &&
                            wyborReceptora &&
                            (pinRecznie && wyborReceptora.osoba.stan_pinu === 'zgodny' ? (
                              <button
                                type="button"
                                onClick={() => setPinRecznie(false)}
                                disabled={isSaving}
                                className="btn btn--ghost btn--sm mt-1.5"
                              >
                                Pobierz PIN z Receptora zamiast tego
                              </button>
                            ) : (
                              <p className="text-xs text-aurora-warning mt-1.5">
                                {zdaniePinuOsoby(wyborReceptora.osoba, Boolean(editingWorker))}
                              </p>
                            ))}
                        </>
                      )}
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4 border-t border-aurora-border">
                      <div>
                        <label className="field__label block mb-1.5">Email</label>
                        <input
                          type="email"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          placeholder="pracownik@firma.pl"
                          className="input"
                        />
                      </div>
                      <div>
                        <label className="field__label block mb-1.5">Telefon</label>
                        <input
                          type="tel"
                          value={formData.phone}
                          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                          placeholder="+48 123 456 789"
                          className="input"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Tab: Komunikacja */}
                {activeModalTab === 'communication' && (
                  <div className="space-y-6 max-w-2xl mx-auto">
                    <div>
                      <label className="field__label block mb-1.5">
                        Język komunikacji <span className="text-orange-400">*</span>
                      </label>
                      <LanguageCombobox
                        value={formData.preferred_language}
                        onChange={(code) => setFormData({ ...formData, preferred_language: code })}
                      />
                      <p className="text-xs text-aurora-subtle mt-1.5">
                        NEXUS będzie komunikował się z pracownikiem w wybranym języku — wyszukaj
                        spośród wszystkich języków świata
                      </p>
                    </div>

                    <div>
                      <label className="field__label block mb-2">Styl komunikacji</label>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {COMMUNICATION_STYLES.map((style) => (
                          <label
                            key={style.value}
                            className={`flex items-start gap-2.5 p-4 rounded-ids-md border cursor-pointer transition-all ${
                              formData.communication_style === style.value
                                ? 'border-aurora-brand bg-blue-500/10 ring-1 ring-blue-500/50'
                                : 'border-aurora-border hover:border-aurora-border-strong hover:bg-aurora-bg-subtle'
                            }`}
                          >
                            <input
                              type="radio"
                              name="communication_style"
                              value={style.value}
                              checked={formData.communication_style === style.value}
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  communication_style: e.target.value,
                                })
                              }
                              className="mt-0.5 text-orange-500 focus:ring-orange-500"
                            />
                            <div>
                              <span className="text-aurora-text text-sm font-medium">
                                {style.label}
                              </span>
                              <p className="text-xs text-aurora-subtle mt-0.5">
                                {style.description}
                              </p>
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Tab: Rola i moduły */}
                {activeModalTab === 'role' && (
                  <div className="space-y-6 max-w-2xl mx-auto">
                    <div>
                      <label className="field__label block mb-1.5">Rola w zespole hali</label>
                      <select
                        value={formData.worker_role}
                        onChange={(e) => setFormData({ ...formData, worker_role: e.target.value })}
                        className="select"
                      >
                        {WORKER_ROLES.map((role) => (
                          <option key={role.value} value={role.value}>
                            {role.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    {editingWorker && canSetOrgRole && currentOrganization ? (
                      <RolaWOrganizacji
                        workerId={editingWorker.id}
                        userId={editingWorker.user_id}
                        organizationId={currentOrganization.organization_id}
                        workerActive={editingWorker.is_active}
                      />
                    ) : null}

                    <div>
                      <label className="field__label block mb-2">Poziom doświadczenia</label>
                      <div className="space-y-2">
                        {EXPERIENCE_LEVELS.map((level) => (
                          <label
                            key={level.value}
                            className={`flex items-start gap-3 p-3 rounded-ids-md border cursor-pointer transition-all ${
                              formData.experience_level === level.value
                                ? 'border-aurora-brand bg-blue-500/10 ring-1 ring-blue-500/50'
                                : 'border-aurora-border hover:border-aurora-border-strong hover:bg-aurora-bg-subtle'
                            }`}
                          >
                            <input
                              type="radio"
                              name="experience_level"
                              value={level.value}
                              checked={formData.experience_level === level.value}
                              onChange={(e) =>
                                setFormData({ ...formData, experience_level: e.target.value })
                              }
                              className="mt-0.5 text-orange-500 focus:ring-orange-500"
                            />
                            <div>
                              <span className="text-aurora-text font-medium">{level.label}</span>
                              <p className="text-xs text-aurora-subtle mt-0.5">
                                {level.description}
                              </p>
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>

                    <div className="pt-4 border-t border-aurora-border">
                      <label className="field__label block mb-2">
                        Dostęp do modułów <span className="text-orange-400">*</span>
                      </label>
                      <div className="grid grid-cols-2 gap-2">
                        {WORKER_MODULES.map((module) => {
                          const isChecked = formData.module_access.includes(module.slug);
                          const Icon = module.icon;
                          return (
                            <label
                              key={module.slug}
                              className={`flex items-center gap-3 p-3 rounded-ids-md border cursor-pointer transition-all ${
                                isChecked
                                  ? 'border-aurora-brand bg-blue-500/10 ring-1 ring-blue-500/50'
                                  : 'border-aurora-border hover:border-aurora-border-strong hover:bg-aurora-bg-subtle'
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={isChecked}
                                onChange={() => handleModuleToggle(module.slug)}
                                className="w-4 h-4 rounded border-gray-500 text-orange-600 focus:ring-orange-500 focus:ring-offset-gray-800"
                              />
                              <div
                                className={`p-2 rounded-ids-md ${module.color.bg} ${module.color.text}`}
                              >
                                <Icon className="w-4 h-4" />
                              </div>
                              <span className="text-aurora-text font-medium">{module.navName}</span>
                            </label>
                          );
                        })}
                      </div>
                      <p className="text-xs text-gray-500 mt-2">
                        Przypisanie decyduje, na czyjej liście pracowników człowiek się pojawia. Te
                        same plakietki da się przełączyć wprost z kafelka i z kartoteki — modal nie
                        jest do tego konieczny.
                      </p>
                    </div>
                  </div>
                )}

                {/* Tab: Zatrudnienie */}
                {activeModalTab === 'contact' && (
                  <div className="space-y-6 max-w-2xl mx-auto">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="field__label block mb-1.5">Forma zatrudnienia</label>
                        <select
                          value={formData.employment_type}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              employment_type: e.target.value,
                              subject_to_labor_code: e.target.value === 'employment',
                            })
                          }
                          className="select"
                        >
                          {EMPLOYMENT_TYPES.map((type) => (
                            <option key={type.value} value={type.value}>
                              {type.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="field__label block mb-1.5">Rodzaj umowy</label>
                        <select
                          value={formData.contract_type}
                          onChange={(e) =>
                            setFormData({ ...formData, contract_type: e.target.value })
                          }
                          className="select"
                        >
                          {CONTRACT_TYPES.map((type) => (
                            <option key={type.value} value={type.value}>
                              {type.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      <div>
                        <label className="field__label block mb-1.5">Data zatrudnienia</label>
                        <input
                          type="date"
                          value={formData.hire_date}
                          onChange={(e) => setFormData({ ...formData, hire_date: e.target.value })}
                          className="input"
                        />
                      </div>
                      <div>
                        <label className="field__label block mb-1.5">Godziny/tydzień</label>
                        <input
                          type="number"
                          min="1"
                          max="60"
                          value={formData.weekly_hours}
                          onChange={(e) =>
                            setFormData({ ...formData, weekly_hours: Number(e.target.value) })
                          }
                          className="input"
                        />
                      </div>
                      <div>
                        <label className="field__label block mb-1.5">Urlop dni/rok</label>
                        <input
                          type="number"
                          min="0"
                          max="50"
                          value={formData.annual_leave_days}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              annual_leave_days: Number(e.target.value),
                            })
                          }
                          className="input"
                        />
                      </div>
                    </div>

                    <div className="space-y-2 pt-4 border-t border-aurora-border">
                      <label
                        className={`flex items-center gap-3 p-3 rounded-ids-md border cursor-pointer transition-all ${
                          formData.can_work_weekends
                            ? 'border-green-500 bg-green-500/10'
                            : 'border-aurora-border hover:border-aurora-border-strong hover:bg-aurora-bg-subtle'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={formData.can_work_weekends}
                          onChange={(e) =>
                            setFormData({ ...formData, can_work_weekends: e.target.checked })
                          }
                          className="w-4 h-4 rounded border-gray-500 text-green-600 focus:ring-green-500 focus:ring-offset-gray-800"
                        />
                        <div className="flex-1">
                          <span className="text-aurora-text text-sm font-medium">
                            Może pracować w niedziele i święta
                          </span>
                        </div>
                      </label>

                      <label
                        className={`flex items-center gap-3 p-3 rounded-ids-md border cursor-pointer transition-all ${
                          formData.available_all_departments
                            ? 'border-blue-500 bg-blue-500/10'
                            : 'border-aurora-border hover:border-aurora-border-strong hover:bg-aurora-bg-subtle'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={formData.available_all_departments}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              available_all_departments: e.target.checked,
                            })
                          }
                          className="w-4 h-4 rounded border-gray-500 text-blue-600 focus:ring-blue-500 focus:ring-offset-gray-800"
                        />
                        <div className="flex-1">
                          <span className="text-aurora-text text-sm font-medium">
                            Dostępny we wszystkich działach
                          </span>
                        </div>
                      </label>

                      <label
                        className={`flex items-center gap-3 p-3 rounded-ids-md border cursor-pointer transition-all ${
                          !formData.subject_to_labor_code
                            ? 'border-yellow-500 bg-yellow-500/10'
                            : 'border-aurora-border hover:border-aurora-border-strong hover:bg-aurora-bg-subtle'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={!formData.subject_to_labor_code}
                          onChange={(e) =>
                            setFormData({ ...formData, subject_to_labor_code: !e.target.checked })
                          }
                          className="w-4 h-4 rounded border-gray-500 text-yellow-600 focus:ring-yellow-500 focus:ring-offset-gray-800"
                        />
                        <div className="flex-1">
                          <span className="text-aurora-text text-sm font-medium">
                            Umowa cywilnoprawna (B2B/zlecenie)
                          </span>
                        </div>
                      </label>
                    </div>
                  </div>
                )}
              </form>
            </div>

            {/* Modal Footer - Fixed */}
            <div className="flex flex-wrap items-center justify-between gap-3 px-6 py-4 border-t border-aurora-border">
              <p className="text-xs text-aurora-subtle">
                <span className="text-orange-400">*</span> Pola wymagane
              </p>
              <div className="flex gap-3">
                <button type="button" onClick={closeModal} className="btn btn--secondary">
                  Anuluj
                </button>
                <button
                  type="submit"
                  form="worker-form"
                  disabled={isSaving}
                  className="btn btn--primary min-w-[120px]"
                >
                  {isSaving ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4" />
                  )}
                  {editingWorker ? 'Zapisz zmiany' : 'Dodaj pracownika'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Odznaki nie mają tu już własnego modala — pokazuje je kartoteka
        pod `/admin/workers/:id`, razem z resztą dorobku i osią czasu. Dwa
        miejsca renderujące ten sam dorobek nieuchronnie by się rozjechały,
        a modal i tak nie dawał się podesłać ani otworzyć w nowej karcie. */}
    </div>
  );
}
